import type { Squad } from '../../../types';
import type {
  Partner,
  PartnerContact,
  PartnerLocation,
  PartnerSeason,
  PartnerSeasonFlow,
} from '../../partners/types';

export const ACTIVE_OPS_FLOW_COLLECTION = 'partnerSeasonFlows';
export const LEGACY_OPS_FLOW_COLLECTION = 'opsFlows';

export function partnerSeasonFlowId(partnerId: string, seasonId: string): string {
  const partner = String(partnerId || '').trim();
  const season = String(seasonId || '').trim();
  if (!partner || !season) throw new Error('partnerId and seasonId are required.');
  return `${partner}__${season}`;
}

export type CanonicalOpsContext = {
  flow: PartnerSeasonFlow;
  partner: Partner | null;
  season: PartnerSeason | null;
  locations: PartnerLocation[];
  squads: Squad[];
  contacts: PartnerContact[];
};

export function contactAppliesToLocation(contact: PartnerContact, locationId: string): boolean {
  const locationIds = Array.isArray(contact.locationIds) ? contact.locationIds.filter(Boolean) : [];
  return locationIds.length === 0 || locationIds.includes(locationId);
}

export function groupSquadsByPartnerSeason(squads: Squad[]): Map<string, Squad[]> {
  const groups = new Map<string, Squad[]>();
  for (const squad of squads) {
    const partnerId = String((squad as any).partnerId || '').trim();
    const seasonId = String((squad as any).seasonId || (squad as any).termId || '').trim();
    if (!partnerId || !seasonId) continue;
    const id = partnerSeasonFlowId(partnerId, seasonId);
    groups.set(id, [...(groups.get(id) || []), squad]);
  }
  return groups;
}

export function isHistoricalOpsFlow(flow: any): boolean {
  return Boolean(flow?.readOnlyHistorical || flow?.migrationSource === 'legacy-ops-flow-snapshot');
}

export function resolveActiveFlowIdentity(
  flow: Pick<PartnerSeasonFlow, 'partnerId' | 'locationIds'>,
  partnerById: Map<string, { name?: string }>,
  locationById: Map<string, { name?: string }>,
): { partnerName: string; locationNames: string[] } {
  const partnerId = String(flow.partnerId || '').trim();
  const partnerName = String(partnerById.get(partnerId)?.name || partnerId || '—').trim() || '—';
  const locationNames = (Array.isArray(flow.locationIds) ? flow.locationIds : [])
    .map((locationId) => String(locationById.get(String(locationId))?.name || locationId).trim())
    .filter(Boolean);
  return { partnerName, locationNames };
}
